import { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Pencil, Loader2, KeyRound, ShieldOff, ShieldCheck, AlertTriangle, Activity, Clock, Coins, Gift } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import {
    Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter, DialogTrigger,
} from './ui/dialog';
import { Tabs, TabsList, TabsTrigger, TabsContent } from './ui/tabs';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const fmtRelativeOrDate = (iso) => {
    if (!iso) return '—';
    try {
        const d = new Date(iso);
        const diff = Date.now() - d.getTime();
        const mins = Math.floor(diff / 60000);
        if (mins < 1) return 'just now';
        if (mins < 60) return `${mins}m ago`;
        const hrs = Math.floor(mins / 60);
        if (hrs < 24) return `${hrs}h ago`;
        const days = Math.floor(hrs / 24);
        if (days < 7) return `${days}d ago`;
        return d.toLocaleString('en-US', { timeZone: 'America/Chicago', month: 'short', day: 'numeric', year: 'numeric' });
    } catch { return iso; }
};

const fmtDateTime = (iso) => {
    if (!iso) return '—';
    try {
        return new Date(iso).toLocaleString('en-US', {
            timeZone: 'America/Chicago',
            month: 'short', day: 'numeric',
            hour: 'numeric', minute: '2-digit',
        });
    } catch { return iso; }
};

const EditUserDialog = ({ user, onUpdated, trigger, size = 'default' }) => {
    const [open, setOpen] = useState(false);
    const [saving, setSaving] = useState(false);
    const [resetting, setResetting] = useState(false);
    const [statusBusy, setStatusBusy] = useState(false);

    const [form, setForm] = useState({
        name: '',
        email: '',
        phone: '',
        birthday: '',
    });
    const [newPassword, setNewPassword] = useState('');
    const [activity, setActivity] = useState(null);
    const [activityLoading, setActivityLoading] = useState(false);

    useEffect(() => {
        if (open && user) {
            setForm({
                name: user.name || '',
                email: user.email || '',
                phone: user.phone || '',
                birthday: user.birthday || '',
            });
            setNewPassword('');
            // Fetch activity snapshot
            (async () => {
                setActivityLoading(true);
                try {
                    const { data } = await axios.get(`${API}/admin/user-activity/${user.id}`, { withCredentials: true });
                    setActivity(data);
                } catch (e) { /* ignore */ }
                setActivityLoading(false);
            })();
        }
    }, [open, user]);

    const upd = (k, v) => setForm((f) => ({ ...f, [k]: v }));

    const saveProfile = async () => {
        setSaving(true);
        try {
            await axios.post(`${API}/admin/user-profile`, {
                user_id: user.id,
                name: form.name,
                email: form.email,
                phone: form.phone,
                birthday: form.birthday,
            }, { withCredentials: true });
            toast.success('Profile updated');
            if (onUpdated) onUpdated();
        } catch (e) {
            toast.error(e.response?.data?.detail || 'Failed to save');
        }
        setSaving(false);
    };

    const resetPassword = async () => {
        if (!newPassword || newPassword.length < 6) {
            toast.error('New password must be at least 6 characters');
            return;
        }
        setResetting(true);
        try {
            await axios.post(`${API}/admin/reset-password`,
                { user_id: user.id, new_password: newPassword },
                { withCredentials: true });
            toast.success(`Password reset for ${user.name || user.email}`);
            setNewPassword('');
        } catch (e) {
            toast.error(e.response?.data?.detail || 'Reset failed');
        }
        setResetting(false);
    };

    const toggleStatus = async () => {
        const next = (user.status === 'suspended') ? 'active' : 'suspended';
        if (next === 'suspended' && !window.confirm(`Suspend ${user.name || user.email}? They won't be able to log in.`)) return;
        setStatusBusy(true);
        try {
            await axios.post(`${API}/admin/user-status`,
                { user_id: user.id, status: next },
                { withCredentials: true });
            toast.success(`Account ${next === 'suspended' ? 'suspended' : 'reactivated'}`);
            if (onUpdated) onUpdated();
        } catch (e) {
            toast.error(e.response?.data?.detail || 'Status update failed');
        }
        setStatusBusy(false);
    };

    const isSuspended = user?.status === 'suspended';

    const defaultTrigger = (
        <Button
            size={size === 'sm' ? 'sm' : 'default'}
            variant="outline"
            className={`border-white/10 text-[#A0AAB2] hover:text-white hover:bg-white/5 ${size === 'sm' ? 'h-7 text-xs' : ''}`}
            data-testid={`edit-user-trigger-${user?.id || ''}`}
        >
            <Pencil className={size === 'sm' ? 'w-3 h-3 mr-1' : 'w-3.5 h-3.5 mr-1'} />
            Edit
        </Button>
    );

    return (
        <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>{trigger || defaultTrigger}</DialogTrigger>
            <DialogContent className="bg-[#0a0a0a] border-white/10 max-w-md max-h-[90vh] overflow-y-auto" data-testid="edit-user-dialog">
                <DialogHeader>
                    <DialogTitle className="text-white">Edit Customer</DialogTitle>
                    <DialogDescription className="text-[#A0AAB2]">
                        Update profile, reset password, or change account status.
                    </DialogDescription>
                </DialogHeader>

                {isSuspended && (
                    <div className="rounded-lg p-2.5 mb-2 bg-[#FF3B30]/10 border border-[#FF3B30]/40 flex items-center gap-2">
                        <AlertTriangle className="w-4 h-4 text-[#FF3B30]" />
                        <span className="text-xs text-[#FF3B30] font-bold">Account is currently SUSPENDED</span>
                    </div>
                )}

                <Tabs defaultValue="edit" className="w-full">
                    <TabsList className="grid grid-cols-2 bg-[#050505] border border-white/10">
                        <TabsTrigger value="edit" className="data-[state=active]:bg-[#FFD700]/15 data-[state=active]:text-[#FFD700]" data-testid="tab-edit">
                            <Pencil className="w-3 h-3 mr-1" /> Edit
                        </TabsTrigger>
                        <TabsTrigger value="activity" className="data-[state=active]:bg-[#00FFFF]/15 data-[state=active]:text-[#00FFFF]" data-testid="tab-activity">
                            <Activity className="w-3 h-3 mr-1" /> Activity
                        </TabsTrigger>
                    </TabsList>

                    <TabsContent value="edit" className="mt-3">
                {/* Profile */}
                <div className="space-y-3">
                    <div>
                        <Label className="text-xs text-[#A0AAB2]">Name</Label>
                        <Input value={form.name} onChange={(e) => upd('name', e.target.value)}
                            className="mt-1 bg-[#050505] border-white/10 text-white"
                            data-testid="edit-user-name" />
                    </div>
                    <div>
                        <Label className="text-xs text-[#A0AAB2]">Email</Label>
                        <Input type="email" value={form.email} onChange={(e) => upd('email', e.target.value)}
                            className="mt-1 bg-[#050505] border-white/10 text-white"
                            data-testid="edit-user-email" />
                    </div>
                    <div>
                        <Label className="text-xs text-[#A0AAB2]">Phone</Label>
                        <Input type="tel" value={form.phone} onChange={(e) => upd('phone', e.target.value)}
                            placeholder="256-555-0100"
                            className="mt-1 bg-[#050505] border-white/10 text-white"
                            data-testid="edit-user-phone" />
                    </div>
                    <div>
                        <Label className="text-xs text-[#A0AAB2]">Birthday <span className="text-[10px] text-[#666]">(18+)</span></Label>
                        <Input type="date" value={form.birthday} onChange={(e) => upd('birthday', e.target.value)}
                            className="mt-1 bg-[#050505] border-white/10 text-white"
                            data-testid="edit-user-birthday" />
                    </div>
                    <Button
                        onClick={saveProfile}
                        disabled={saving}
                        className="w-full bg-[#FFD700] text-black hover:bg-[#FFD700]/80 font-bold"
                        data-testid="edit-user-save"
                    >
                        {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Save Profile'}
                    </Button>
                </div>

                {/* Password reset */}
                <div className="border-t border-white/10 pt-3 mt-2 space-y-2">
                    <Label className="text-xs text-[#A0AAB2] flex items-center gap-1">
                        <KeyRound className="w-3 h-3" /> Reset Password
                    </Label>
                    <div className="flex gap-2">
                        <Input
                            type="text"
                            value={newPassword}
                            onChange={(e) => setNewPassword(e.target.value)}
                            placeholder="New password (min 6 chars)"
                            className="bg-[#050505] border-white/10 text-white text-sm"
                            data-testid="edit-user-new-password"
                        />
                        <Button
                            onClick={resetPassword}
                            disabled={resetting || !newPassword}
                            className="bg-[#00FFFF]/20 text-[#00FFFF] hover:bg-[#00FFFF]/30 border border-[#00FFFF]/40 whitespace-nowrap"
                            data-testid="edit-user-reset-password"
                        >
                            {resetting ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : 'Reset'}
                        </Button>
                    </div>
                    <p className="text-[10px] text-[#666]">Player keeps this exact password — no forced change on next login.</p>
                </div>

                {/* Status toggle */}
                <div className="border-t border-white/10 pt-3 mt-2">
                    <Label className="text-xs text-[#A0AAB2]">Account Status</Label>
                    <Button
                        onClick={toggleStatus}
                        disabled={statusBusy}
                        className={`w-full mt-1 font-bold ${
                            isSuspended
                                ? 'bg-[#39FF14]/20 text-[#39FF14] hover:bg-[#39FF14]/30 border border-[#39FF14]/40'
                                : 'bg-[#FF3B30]/20 text-[#FF3B30] hover:bg-[#FF3B30]/30 border border-[#FF3B30]/40'
                        }`}
                        data-testid="edit-user-toggle-status"
                    >
                        {statusBusy ? <Loader2 className="w-4 h-4 animate-spin" /> : (
                            isSuspended ? <><ShieldCheck className="w-4 h-4 mr-1.5" />Reactivate Account</>
                                       : <><ShieldOff className="w-4 h-4 mr-1.5" />Suspend Account</>
                        )}
                    </Button>
                </div>
                    </TabsContent>

                    {/* Activity tab */}
                    <TabsContent value="activity" className="mt-3 space-y-3" data-testid="activity-content">
                        {activityLoading ? (
                            <div className="flex justify-center py-8">
                                <Loader2 className="w-5 h-5 text-[#00FFFF] animate-spin" />
                            </div>
                        ) : !activity ? (
                            <p className="text-center text-[#A0AAB2] py-6 text-sm">No activity data available.</p>
                        ) : (
                            <>
                                {/* Stats grid */}
                                <div className="grid grid-cols-2 gap-2">
                                    <div className="rounded-lg p-2.5 bg-[#0a0a0a] border border-white/5">
                                        <p className="text-[9px] text-[#A0AAB2] uppercase tracking-wider">Credits</p>
                                        <p className="text-lg font-black text-[#39FF14]">${(activity.credits || 0).toFixed(2)}</p>
                                    </div>
                                    <div className="rounded-lg p-2.5 bg-[#0a0a0a] border border-white/5">
                                        <p className="text-[9px] text-[#A0AAB2] uppercase tracking-wider">VIP Tier</p>
                                        <p className="text-lg font-black text-[#FFD700]">{activity.vip_level ?? 0}</p>
                                    </div>
                                    <div className="rounded-lg p-2.5 bg-[#0a0a0a] border border-white/5">
                                        <p className="text-[9px] text-[#A0AAB2] uppercase tracking-wider">Wagered</p>
                                        <p className="text-sm font-black text-white">${(activity.total_wagered || 0).toFixed(2)}</p>
                                    </div>
                                    <div className="rounded-lg p-2.5 bg-[#0a0a0a] border border-white/5">
                                        <p className="text-[9px] text-[#A0AAB2] uppercase tracking-wider">Won</p>
                                        <p className="text-sm font-black text-white">${(activity.total_won || 0).toFixed(2)}</p>
                                    </div>
                                </div>

                                {/* Login + signup */}
                                <div className="rounded-lg p-3 bg-[#0a0a0a] border border-white/5 space-y-1.5">
                                    <div className="flex items-center gap-2 text-xs">
                                        <Clock className="w-3 h-3 text-[#00FFFF]" />
                                        <span className="text-[#A0AAB2]">Last login:</span>
                                        <span className="text-white font-medium">{fmtRelativeOrDate(activity.last_login_date)}</span>
                                    </div>
                                    <div className="flex items-center gap-2 text-xs">
                                        <Clock className="w-3 h-3 text-[#666]" />
                                        <span className="text-[#A0AAB2]">Joined:</span>
                                        <span className="text-white font-medium">{fmtDateTime(activity.created_at)}</span>
                                    </div>
                                    {activity.referral_count > 0 && (
                                        <div className="flex items-center gap-2 text-xs">
                                            <Activity className="w-3 h-3 text-[#39FF14]" />
                                            <span className="text-[#A0AAB2]">Referrals:</span>
                                            <span className="text-[#39FF14] font-medium">{activity.referral_count}</span>
                                        </div>
                                    )}
                                </div>

                                {/* Bonus rollover (if pending) */}
                                {Number(activity.pending_rollover || 0) > 0 && (
                                    <div className="rounded-lg p-3 bg-[#FFD700]/8 border border-[#FFD700]/30 flex items-center justify-between gap-3" data-testid="activity-rollover-block">
                                        <div className="flex items-center gap-2 text-xs">
                                            <Gift className="w-4 h-4 text-[#FFD700]" />
                                            <span className="text-[#A0AAB2]">Pending bonus rollover:</span>
                                            <span className="text-[#FFD700] font-bold font-mono">${Number(activity.pending_rollover).toFixed(2)}</span>
                                        </div>
                                        <Button
                                            size="sm"
                                            variant="outline"
                                            onClick={async () => {
                                                if (!window.confirm(`Clear $${Number(activity.pending_rollover).toFixed(2)} pending rollover for ${user.name}? This lets them cash out immediately.`)) return;
                                                try {
                                                    const { data } = await axios.post(`${API}/admin/clear-rollover`,
                                                        { user_id: user.id }, { withCredentials: true });
                                                    toast.success(`Cleared $${Number(data.cleared_amount).toFixed(2)} pending rollover`);
                                                    setActivity({ ...activity, pending_rollover: 0 });
                                                    if (onUpdated) onUpdated();
                                                } catch (e) {
                                                    toast.error(e.response?.data?.detail || 'Failed to clear rollover');
                                                }
                                            }}
                                            className="border-[#FFD700]/40 text-[#FFD700] hover:bg-[#FFD700]/10 h-7 text-xs px-2"
                                            data-testid="activity-clear-rollover-btn"
                                        >
                                            Clear
                                        </Button>
                                    </div>
                                )}

                                {/* Last 5 transactions */}
                                <div>
                                    <Label className="text-xs text-[#A0AAB2] flex items-center gap-1 mb-1.5">
                                        <Coins className="w-3 h-3" /> Last 5 transactions
                                    </Label>
                                    {activity.transactions.length === 0 ? (
                                        <p className="text-xs text-[#666] italic py-2">No transactions yet.</p>
                                    ) : (
                                        <div className="space-y-1.5">
                                            {activity.transactions.map((t, i) => (
                                                <div key={i}
                                                    className="flex items-center justify-between gap-2 px-2.5 py-1.5 rounded bg-[#0a0a0a] border border-white/5 text-xs"
                                                    data-testid={`activity-tx-${i}`}
                                                >
                                                    <div className="min-w-0 flex-1">
                                                        <p className="text-white truncate">{t.description || t.type}</p>
                                                        <p className="text-[10px] text-[#A0AAB2]">{fmtDateTime(t.created_at)}</p>
                                                    </div>
                                                    <span className={`font-mono font-bold flex-shrink-0 ${
                                                        (t.amount || 0) >= 0 ? 'text-[#39FF14]' : 'text-[#FF3B30]'
                                                    }`}>
                                                        {(t.amount || 0) >= 0 ? '+' : ''}${(t.amount || 0).toFixed(2)}
                                                    </span>
                                                </div>
                                            ))}
                                        </div>
                                    )}
                                </div>

                                {/* Free-play / Rollover history */}
                                {Array.isArray(activity.promos) && activity.promos.length > 0 && (
                                    <div data-testid="activity-promo-history">
                                        <Label className="text-xs text-[#A0AAB2] flex items-center gap-1 mb-1.5">
                                            <Gift className="w-3 h-3 text-[#FF007F]" /> Free play history (last 5)
                                        </Label>
                                        <div className="space-y-1.5">
                                            {activity.promos.map((p, i) => (
                                                <div key={i}
                                                    className="flex items-center justify-between gap-2 px-2.5 py-1.5 rounded bg-[#0a0a0a] border border-white/5 text-xs"
                                                    data-testid={`activity-promo-${i}`}
                                                >
                                                    <div className="min-w-0 flex-1">
                                                        <p className="text-white truncate">{p.reason || 'Free play'}</p>
                                                        <p className="text-[10px] text-[#A0AAB2]">
                                                            {fmtDateTime(p.created_at)}
                                                            {p.rollover_multiplier > 0 && (
                                                                <span className="ml-1.5 text-[#FFD700]">
                                                                    · {p.rollover_multiplier}× rollover (${p.rollover_amount.toFixed(2)})
                                                                </span>
                                                            )}
                                                            {p.rollover_multiplier === 0 && (
                                                                <span className="ml-1.5 text-[#39FF14]">· no rollover</span>
                                                            )}
                                                        </p>
                                                    </div>
                                                    <span className="font-mono font-bold flex-shrink-0 text-[#FF007F]">
                                                        +${p.amount.toFixed(2)}
                                                    </span>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                )}
                            </>
                        )}
                    </TabsContent>
                </Tabs>

                <DialogFooter>
                    <Button variant="ghost" onClick={() => setOpen(false)} className="text-[#A0AAB2]">
                        Close
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
};

export default EditUserDialog;
